/* eslint-disable camelcase */
import { IconName } from "@fortawesome/fontawesome-svg-core";
import { ArchiveDeleteResponse } from "components/common/ArchiveDialog/ArchiveDialog";
import { SWRConfiguration } from "swr";
import { ApiArrayResponse, ApiResponse, ServerResponse } from "./axiosConfig";
import { useSwr, axiosInst } from "./axiosWIS";
import { Attribute } from "./hvsBase";

export interface IconData {
  color?: string;
  icon?: IconName | string | null;
  icon_type?: "fontawesome" | "attachment_id" | "url" | "none";
  icon_attachment?: string | null;
  icon_url?: string;
  url?: string;
}

export interface IncidentApiResponse<T> {
  data: T;
}

export interface IncidentType {
  id: string;
  pk_inc_type_id: number;
  inc_type_name: string;
  inc_type_desc: string;
  category?: {
    cat_name: string;
    cat_desc: string;
  };
  subcategory?: {
    sub_cat_name: string;
    sub_cat_desc: string;
  };
  fk_cat_id: number;
  fk_sub_cat_id: number;

  inc_type_sop_map: number[];
  appearance: IconData;
  attributes: Record<string, Attribute>;
  sops: number[];
  incident_count: number;
  inc_status: string;
  has_advanced_insights: false;
  created_by: string;
  create_timestamp: string;
  update_by: string;
  update_timestamp: string;

  version: number;

  refresh_intervals: number;
  has_timeline: boolean;
}

// need to override attributes due the data structure required to manipulate the data
export interface AttributeFormData extends Omit<Attribute, "appearance"> {
  visibility?: "hidden" | "always" | "only_detail" | "";
}
export interface IncidentTypeFormData extends Omit<IncidentType, "attributes"> {
  // need to override attributes due the data structure required to manipulate the data
  attributes: AttributeFormData[];
}

const baseKey = "/api/IncidentTypes";

export const useIncidentTypes = (
  params: {
    pageNr?: number;
    pageSize?: number;
    orderBy?: string;
    Order?: string;
    SearchKey?: string;
  } = {
    pageNr: 1,
    pageSize: 200,
    orderBy: undefined,
    Order: "desc",
    SearchKey: undefined,
  },
  config?: SWRConfiguration,
  requestBody?: Record<string, unknown>,
  usePost?: boolean
): ServerResponse<ApiArrayResponse<IncidentType>> => {
  const url = `${baseKey}${usePost ? "/filter" : ""}?pageNr=${
    params.pageNr
  }&pageSize=${params.pageSize}${
    params.orderBy != null
      ? `&orderBy=${params.orderBy}&Order=${params.Order}`
      : ""
  }${params.SearchKey != null ? `&SearchKey=${params.SearchKey}` : ""}`;
  return useSwr<ApiArrayResponse<IncidentType>>(url, config, requestBody);
};

export const useIncidentType = (
  id?: string | number,
  config?: SWRConfiguration
): ServerResponse<ApiResponse<IncidentType>> =>
  useSwr<ApiResponse<IncidentType>>(id ? `${baseKey}/${id}` : "", config);

export const addIncidentType = async (
  data: Partial<IncidentType>
): Promise<unknown> => {
  const res = await axiosInst.post(baseKey, data);
  return res.data;
};

export const editIncidentType = async (
  data: Partial<IncidentType>
): Promise<ApiResponse<IncidentType>> => {
  const res = await axiosInst.put(`${baseKey}/${data.pk_inc_type_id}`, data);
  return res.data;
};

export const archiveIncidentType = async (
  incidentTypeId: string
): Promise<ArchiveDeleteResponse<number>> => {
  const res = await axiosInst.delete(`${baseKey}/${incidentTypeId}`);
  return res.data;
};
